import React, { useState } from 'react';
import { Star, X, Sparkles, Send, CheckCircle2, Heart, User, Mail, Tag, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../config/api';

const RATING_LABELS = {
  1: 'Needs Improvement',
  2: 'Fair Experience',
  3: 'Good Experience',
  4: 'Great Experience',
  5: 'Outstanding Experience! 🎉'
};

const SUGGESTED_ROLES = [
  'Community Member',
  'Event Attendee',
  'Volunteer',
  'Music Student / Parent',
  'Supporter / Donor'
];

export default function FeedbackModal({ isOpen, onClose, onFeedbackSubmitted }) {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Community Member');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!comment.trim()) {
      toast.error('Please enter your feedback comments.');
      return;
    }

    if (rating < 1 || rating > 5) {
      toast.error('Please select a rating between 1 and 5 stars.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: isAnonymous ? 'Anonymous' : (name.trim() || 'Community Member'),
        email: email.trim(),
        role: role.trim() || 'Community Member',
        rating: Number(rating),
        comment: comment.trim()
      };

      const res = await api.post('/feedback', payload);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit feedback');
      }

      setIsSuccess(true);
      toast.success('Thank you! Your feedback was submitted successfully.');
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted(data.feedback);
      }
    } catch (err) {
      console.error('Feedback submit error:', err);
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setIsSuccess(false);
    setRating(5);
    setHoverRating(0);
    setName('');
    setEmail('');
    setRole('Community Member');
    setComment('');
    setIsAnonymous(false);
    onClose();
  };

  const currentDisplayRating = hoverRating || rating;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 md:p-6 bg-black/60 backdrop-blur-sm overflow-y-auto animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleResetAndClose();
      }}
    >
      <div 
        className="relative w-full max-w-xl bg-white rounded-3xl shadow-2xl border border-orange-100 overflow-hidden my-auto animate-scaleUp transform transition-all"
        role="dialog"
        aria-modal="true"
      >
        {/* Header with gradient banner */}
        <div className="relative bg-gradient-to-r from-orange-500 via-amber-500 to-red-500 p-5 sm:p-7 text-white overflow-hidden">
          {/* Subtle background glow */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
          <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-yellow-300/20 rounded-full blur-xl pointer-events-none"></div>

          <button
            onClick={handleResetAndClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors duration-200 focus:outline-none"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2 text-amber-200 text-xs sm:text-sm font-semibold uppercase tracking-wider mb-1">
            <Sparkles className="w-4 h-4" />
            <span>Community Voice</span>
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight">
            Share Your Experience
          </h2>
          <p className="text-white/90 text-xs sm:text-sm mt-1 max-w-md">
            Your reviews help us grow, inspire others, and build stronger cultural programs for everyone!
          </p>
        </div>

        {/* Modal Body */}
        <div className="p-5 sm:p-7 max-h-[80vh] overflow-y-auto">
          {isSuccess ? (
            <div className="text-center py-6 sm:py-8 space-y-4">
              <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shadow-inner animate-bounce">
                <CheckCircle2 className="w-10 h-10 sm:w-12 sm:h-12" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                Thank You for Your Feedback!
              </h3>
              <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto leading-relaxed">
                Your feedback has been received and will appear on the homepage once reviewed by our team. We appreciate your support!
              </p>
              <div className="pt-4">
                <button
                  type="button"
                  onClick={handleResetAndClose}
                  className="px-6 py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold shadow-md hover:from-orange-600 hover:to-amber-600 transition-all duration-200 hover:scale-105"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              {/* Star Rating Section */}
              <div className="text-center bg-gradient-to-br from-amber-50/70 to-orange-50/60 p-4 rounded-2xl border border-amber-100">
                <label className="block text-xs sm:text-sm font-bold text-gray-800 uppercase tracking-wider mb-2">
                  Your Overall Rating
                </label>
                <div className="flex items-center justify-center gap-1.5 sm:gap-2 my-2">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const isFilled = star <= currentDisplayRating;
                    return (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="p-1 sm:p-1.5 transition-transform duration-150 hover:scale-125 focus:outline-none group"
                        aria-label={`${star} star rating`}
                      >
                        <Star
                          className={`w-7 h-7 sm:w-9 sm:h-9 transition-colors duration-200 ${
                            isFilled
                              ? 'text-amber-400 fill-amber-400 drop-shadow-[0_2px_8px_rgba(251,191,36,0.5)]'
                              : 'text-gray-300 group-hover:text-amber-200'
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
                <div className="text-xs sm:text-sm font-semibold text-amber-700 min-h-[20px] transition-all">
                  {RATING_LABELS[currentDisplayRating] || ''}
                </div>
              </div>

              {/* Feedback Comment */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4 text-orange-500" />
                    Your Review & Comments <span className="text-red-500">*</span>
                  </span>
                  <span className="text-[11px] text-gray-400 font-normal">
                    {comment.length}/500
                  </span>
                </label>
                <textarea
                  required
                  rows={4}
                  maxLength={500}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share what you enjoyed, the impact of our events, workshops, or your message for Beats of Washington..."
                  className="w-full px-3.5 py-2.5 text-sm sm:text-base border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all placeholder:text-gray-400 resize-none shadow-sm"
                />
              </div>

              {/* Name & Anonymous Toggle */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <User className="w-4 h-4 text-orange-500" />
                    Your Name
                  </label>
                  <input
                    type="text"
                    disabled={isAnonymous}
                    value={isAnonymous ? '' : name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={isAnonymous ? 'Anonymous' : 'e.g. John Doe'}
                    className={`w-full px-3.5 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${
                      isAnonymous ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white'
                    }`}
                  />
                  <div className="mt-1.5 flex items-center">
                    <input
                      type="checkbox"
                      id="anonCheck"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-500 w-3.5 h-3.5"
                    />
                    <label htmlFor="anonCheck" className="ml-1.5 text-xs text-gray-500 cursor-pointer select-none">
                      Post as Anonymous
                    </label>
                  </div>
                </div>

                {/* Role / Tagline */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Tag className="w-4 h-4 text-orange-500" />
                    Who are you? (Optional)
                  </label>
                  <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g. Event Attendee, Volunteer"
                    className="w-full px-3.5 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  />
                  {/* Quick role suggestions */}
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {SUGGESTED_ROLES.slice(0, 3).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                          role === r
                            ? 'bg-orange-500 text-white font-medium'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Email (Private) */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-4 h-4 text-orange-500" />
                    Email Address <span className="text-[11px] font-normal text-gray-400">(Optional - will never be displayed publicly)</span>
                  </span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full px-3.5 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                />
              </div>

              {/* Submit Buttons */}
              <div className="pt-2 sm:pt-3 flex items-center justify-end gap-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={handleResetAndClose}
                  className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-orange-500 via-amber-500 to-red-500 text-white font-bold text-sm shadow-md hover:from-orange-600 hover:to-red-600 transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Submit Review</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
